import {
  Box,
  ButtonBase,
  Link as MuiLink,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
} from "@mui/material";
import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";

import { groupEdge, monospaceStyle } from "../../../theme";
import { Matrix, MatrixColumns, MatrixRows } from "../../../types/analytics";
import { EntityClickHandler } from "../../../types/entityRegistry";
import { AnalyticsSelection, exploreAddress } from "../analyticsState";
import { columnSelection, groupColumns } from "./matrixModel";
import { AggregateRow, ProductRow, Slot, TotalsRow } from "./MatrixRows";

const FIRST_COLUMN_WIDTH = 300;
const END_COLUMN_WIDTH = 72;
const COLUMN_MIN_WIDTH = 92;
// The height of the row of groups, under which the row of columns sticks
const GROUP_ROW_HEIGHT = 28;

const ROW_HEAD: Record<MatrixRows, string> = {
  products: "product · maker · country",
  categories: "category · products",
  makers: "maker · products",
  capabilities: "capability · products",
};

const ROW_NOUN: Record<MatrixRows, string> = {
  products: "Products",
  categories: "Categories",
  makers: "Makers",
  capabilities: "Capabilities",
};

const COLUMN_NOUN: Record<MatrixColumns, string> = {
  capabilities: "capabilities",
  conditions: "conditions",
  modes: "modes",
};

interface MatrixTableProps {
  matrix: Matrix;
  selection: AnalyticsSelection;
  onPin: (id: string | null) => void;
  onOpenEntity: EntityClickHandler;
}

// The table: a head of effect areas (each folds) over the columns with their
// counts, then a row for each product, or for each class, maker or capability,
// and, under counts, the totals of the whole scope. It scrolls sideways with
// its first column fixed.
const MatrixTable: React.FC<MatrixTableProps> = ({ matrix, selection, onPin, onOpenEntity }) => {
  // The groups folded into one column; kept while the page changes
  const [folded, setFolded] = useState<ReadonlySet<string>>(new Set());
  const toggle = (key: string) =>
    setFolded((current) => {
      const next = new Set(current);
      if (!next.delete(key)) {
        next.add(key);
      }
      return next;
    });

  const groups = useMemo(() => groupColumns(matrix.shown_columns), [matrix.shown_columns]);
  const slots = useMemo<Slot[]>(
    () =>
      groups.flatMap((group): Slot[] =>
        folded.has(group.key) && group.columns.length > 1
          ? [{ key: group.key, columns: group.columns, folded: true, groupStart: true }]
          : group.columns.map((column, position) => ({
              key: column.id,
              columns: [column],
              folded: false,
              groupStart: position === 0,
            })),
      ),
    [groups, folded],
  );

  const isProducts = matrix.rows === "products";
  // The products that have a row, whatever the narrowing: a column head counts
  // against these
  const listed = matrix.evidence.n - matrix.unlisted;
  const floorTitle = `Under the floor of ${matrix.floors.min_support} products`;

  const headCellSx = {
    backgroundColor: "background.paper",
    color: "text.secondary",
    fontWeight: 500,
    fontSize: 12,
    lineHeight: 1.25,
    textAlign: "center",
    verticalAlign: "bottom",
    p: "8px 6px 6px",
  } as const;

  return (
    <Box
      sx={{
        overflow: "auto",
        maxHeight: "calc(100vh - 240px)",
        minHeight: 160,
        position: "relative",
        border: 1,
        borderColor: "divider",
        borderRadius: (theme) => `${theme.radius.surface}px`,
        backgroundColor: "background.paper",
      }}
    >
      <Table
        stickyHeader
        size="small"
        aria-label={`${ROW_NOUN[matrix.rows]} by ${COLUMN_NOUN[matrix.columns]}`}
        sx={{
          borderCollapse: "separate",
          borderSpacing: 0,
          tableLayout: "fixed",
          width: "100%",
          minWidth: FIRST_COLUMN_WIDTH + END_COLUMN_WIDTH + slots.length * COLUMN_MIN_WIDTH,
          "& .MuiTableCell-root": {
            borderBottom: "1px solid",
            borderBottomColor: "divider",
          },
        }}
      >
        <colgroup>
          <col style={{ width: FIRST_COLUMN_WIDTH }} />
          {slots.map((slot) => (
            <col key={slot.key} />
          ))}
          <col style={{ width: END_COLUMN_WIDTH }} />
        </colgroup>
        <TableHead>
          <TableRow>
            <TableCell
              sx={{ ...headCellSx, height: GROUP_ROW_HEIGHT, left: 0, zIndex: 4, top: 0 }}
            />
            {groups.map((group) => {
              const isFolded = folded.has(group.key) && group.columns.length > 1;
              return (
                <TableCell
                  key={group.key}
                  colSpan={isFolded ? 1 : group.columns.length}
                  sx={{
                    ...headCellSx,
                    top: 0,
                    height: GROUP_ROW_HEIGHT,
                    p: "6px 8px 4px",
                    textAlign: "left",
                    whiteSpace: "nowrap",
                    textTransform: "uppercase",
                    letterSpacing: "0.06em",
                    fontSize: 10.5,
                    color: "text.disabled",
                    borderLeft: groupEdge,
                    ...(isFolded ? { backgroundColor: "action.hover" } : {}),
                  }}
                >
                  {group.name !== null && group.columns.length > 1 ? (
                    <ButtonBase
                      onClick={() => toggle(group.key)}
                      aria-label={`${isFolded ? "Unfold" : "Fold"} ${group.name}`}
                      aria-expanded={!isFolded}
                      sx={{
                        font: "inherit",
                        letterSpacing: "inherit",
                        textTransform: "inherit",
                        color: "inherit",
                        gap: 0.75,
                        maxWidth: "100%",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                      }}
                    >
                      {group.name}
                      <span aria-hidden>{isFolded ? "▸" : "▾"}</span>
                    </ButtonBase>
                  ) : (
                    group.name
                  )}
                </TableCell>
              );
            })}
            <TableCell sx={{ ...headCellSx, top: 0, height: GROUP_ROW_HEIGHT }} />
          </TableRow>
          <TableRow>
            <TableCell
              sx={{
                ...headCellSx,
                top: GROUP_ROW_HEIGHT,
                left: 0,
                zIndex: 4,
                textAlign: "left",
                pl: "12px",
                fontWeight: 400,
                color: "text.disabled",
              }}
            >
              {ROW_HEAD[matrix.rows]}
            </TableCell>
            {slots.map((slot) => {
              if (slot.folded) {
                return (
                  <TableCell
                    key={slot.key}
                    scope="col"
                    sx={{
                      ...headCellSx,
                      top: GROUP_ROW_HEIGHT,
                      borderLeft: groupEdge,
                      backgroundColor: "action.hover",
                    }}
                  >
                    any of {slot.columns.length}
                  </TableCell>
                );
              }
              const column = slot.columns[0];
              const label = column.entity.name ?? column.entity.id;
              return (
                <TableCell
                  key={slot.key}
                  scope="col"
                  sx={{
                    ...headCellSx,
                    top: GROUP_ROW_HEIGHT,
                    ...(slot.groupStart ? { borderLeft: groupEdge } : {}),
                  }}
                >
                  {label}
                  {column.qualifier && (
                    <Box component="span" sx={{ display: "block", color: "text.disabled", fontSize: 11 }}>
                      {column.qualifier.name ?? column.qualifier.id}
                    </Box>
                  )}
                  <Box
                    component="span"
                    data-thin={column.thin}
                    title={column.thin ? floorTitle : undefined}
                    sx={{
                      ...monospaceStyle,
                      display: "block",
                      mt: "3px",
                      fontSize: 11,
                      fontWeight: 400,
                      color: "text.disabled",
                      opacity: column.thin ? 0.6 : 1,
                    }}
                  >
                    <MuiLink
                      component={Link}
                      to={exploreAddress(
                        columnSelection(selection, column, matrix.columns, matrix.grain),
                      )}
                      underline="hover"
                      color="inherit"
                      title={column.thin ? floorTitle : undefined}
                    >
                      {isProducts ? `${column.product_count} of ${listed}` : column.product_count}
                    </MuiLink>
                  </Box>
                </TableCell>
              );
            })}
            <TableCell sx={{ ...headCellSx, top: GROUP_ROW_HEIGHT }}>
              {isProducts ? "claims" : "products"}
              {isProducts && matrix.sort === "count" && <span aria-hidden> ▼</span>}
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {isProducts
            ? matrix.product_rows.map((row) => (
                <ProductRow
                  key={row.id}
                  row={row}
                  slots={slots}
                  onPin={onPin}
                  onOpenEntity={onOpenEntity}
                />
              ))
            : matrix.aggregate_rows.map((row) => (
                <AggregateRow
                  key={row.id}
                  matrix={matrix}
                  row={row}
                  slots={slots}
                  selection={selection}
                  onPin={onPin}
                  onOpenEntity={onOpenEntity}
                />
              ))}
          {!isProducts && <TotalsRow matrix={matrix} slots={slots} />}
        </TableBody>
      </Table>
    </Box>
  );
};

export default MatrixTable;
